import { useEffect, useRef } from 'react';
import { CANVAS_SIZE, getLogicalCoords } from '../lib/canvas-utils';
import { tintFilter } from '../lib/tints';

export const CanvasLayer = ({
  layer,
  selected,
  canvasRef,
  onSelect,
  onChange,
  onInteractionStart,
}) => {
  const stateRef = useRef(null);
  const elRef = useRef(null);

  useEffect(() => {
    const handleMove = (e) => {
      const s = stateRef.current;
      if (!s || !canvasRef.current) return;
      e.preventDefault();
      const { x, y } = getLogicalCoords(e.clientX, e.clientY, canvasRef.current);

      if (s.mode === 'move') {
        const dx = x - s.startMouse.x;
        const dy = y - s.startMouse.y;
        onChange(layer.id, {
          x: s.startLayer.x + dx,
          y: s.startLayer.y + dy,
        });
      } else if (s.mode === 'resize') {
        const cx = s.startLayer.x + s.startLayer.width / 2;
        const cy = s.startLayer.y + s.startLayer.height / 2;
        const initDist = Math.hypot(s.startMouse.x - cx, s.startMouse.y - cy);
        const newDist = Math.hypot(x - cx, y - cy);
        const scale = Math.max(0.05, newDist / Math.max(1, initDist));
        // Lock aspect ratio to the image's natural dimensions
        const natAR =
          s.startLayer.naturalWidth && s.startLayer.naturalHeight
            ? s.startLayer.naturalWidth / s.startLayer.naturalHeight
            : s.startLayer.width / Math.max(1, s.startLayer.height);
        let newW = Math.max(20, s.startLayer.width * scale);
        let newH = newW / natAR;
        if (newH < 20) {
          newH = 20;
          newW = newH * natAR;
        }
        onChange(layer.id, {
          width: newW,
          height: newH,
          x: cx - newW / 2,
          y: cy - newH / 2,
        });
      } else if (s.mode === 'rotate') {
        const cx = s.startLayer.x + s.startLayer.width / 2;
        const cy = s.startLayer.y + s.startLayer.height / 2;
        const initAngle = Math.atan2(s.startMouse.y - cy, s.startMouse.x - cx);
        const curAngle = Math.atan2(y - cy, x - cx);
        const deltaDeg = ((curAngle - initAngle) * 180) / Math.PI;
        let next = s.startLayer.rotation + deltaDeg;
        if (e.shiftKey) next = Math.round(next / 15) * 15;
        onChange(layer.id, { rotation: next });
      }
    };

    const handleUp = () => {
      if (stateRef.current) {
        document.body.classList.remove('dragging');
        stateRef.current = null;
      }
    };

    window.addEventListener('pointermove', handleMove, { passive: false });
    window.addEventListener('pointerup', handleUp);
    window.addEventListener('pointercancel', handleUp);
    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
      window.removeEventListener('pointercancel', handleUp);
    };
  }, [layer.id, onChange, canvasRef]);

  const startInteraction = (mode) => (e) => {
    if (!canvasRef.current) return;
    e.stopPropagation();
    e.preventDefault();
    onSelect(layer.id);
    // Snapshot history BEFORE any pixels are mutated
    if (onInteractionStart) onInteractionStart();
    const { x, y } = getLogicalCoords(e.clientX, e.clientY, canvasRef.current);
    stateRef.current = {
      mode,
      startMouse: { x, y },
      startLayer: { ...layer },
    };
    document.body.classList.add('dragging');
  };

  return (
    <div
      ref={elRef}
      data-testid={`canvas-layer-${layer.id}`}
      data-selected={selected ? 'true' : 'false'}
      onPointerDown={startInteraction('move')}
      style={{
        position: 'absolute',
        left: `${(layer.x / CANVAS_SIZE) * 100}%`,
        top: `${(layer.y / CANVAS_SIZE) * 100}%`,
        width: `${(layer.width / CANVAS_SIZE) * 100}%`,
        height: `${(layer.height / CANVAS_SIZE) * 100}%`,
        transform: `rotate(${layer.rotation}deg)`,
        transformOrigin: 'center center',
        zIndex: layer.zIndex + 1,
        cursor: 'grab',
        touchAction: 'none',
      }}
    >
      <img
        src={layer.src}
        alt=""
        draggable={false}
        crossOrigin="anonymous"
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          pointerEvents: 'none',
          userSelect: 'none',
          filter: tintFilter(layer.tint),
        }}
      />

      {selected && (
        <>
          <div
            style={{
              position: 'absolute',
              inset: 0,
              border: '2px dashed #0A0A0A',
              pointerEvents: 'none',
            }}
          />
          {[
            { name: 'tl', style: { left: -7, top: -7, cursor: 'nwse-resize' } },
            { name: 'tr', style: { right: -7, top: -7, cursor: 'nesw-resize' } },
            { name: 'bl', style: { left: -7, bottom: -7, cursor: 'nesw-resize' } },
            { name: 'br', style: { right: -7, bottom: -7, cursor: 'nwse-resize' } },
          ].map((h) => (
            <div
              key={h.name}
              data-testid={`resize-handle-${h.name}`}
              className="handle"
              style={h.style}
              onPointerDown={startInteraction('resize')}
            />
          ))}
          <div
            data-testid="rotate-handle"
            className="handle handle-rotate"
            style={{ left: '50%', top: -32, transform: 'translateX(-50%)', cursor: 'grab' }}
            onPointerDown={startInteraction('rotate')}
          />
        </>
      )}
    </div>
  );
};
